import {
  parseProgramFromPlanId,
  parsePlansList,
  parseProgramsList,
  resolveProgramSession,
  type Locale,
  type Plan as PlanT,
  type PlannedExerciseEntry,
  type Program as ProgramT,
} from '@jackedlogs/shared';

import type { ProgramApiFetch } from '../ProgramEditor/types';
import { asyncStorageResourceCache } from './state/asyncListCache';

// LG-30 — the ONE source for the plan/program TEMPLATE behind a logged
// session. Both cue surfaces read it: the per-exercise + per-set cues
// (usePlanCues) and the workout-level coach notes (useCoachNotes), which
// each used to hand-roll their own copy of this resolution.
//
// The template is never snapshotted onto the session
// (feedback_no_denormalized_display_snapshots): the cue follows the slug and
// is re-resolved at render, which is what makes it correct across
// fresh-start / EDIT / REPEAT and across an in-workout exercise swap.
//
// CACHE-FIRST, then the server. The cache-only version of this resolution
// failed silently in two ways, both of which cost the athlete the coach's
// instruction with no trace anywhere:
//   1. COLD CACHE. 'plan/programs' is written by the Plan tab. On a device
//      where that tab was never opened (fresh install, cleared data) the
//      lookup missed and returned nothing. The logger screen itself mounts
//      useInWorkoutTargets, which fetches the programs and warms that very
//      cache — but it lands AFTER this read, and the read never retried.
//   2. A KEY NOBODY WRITES. 'plan/list' (single-day plans) is registered by
//      no mobile surface at all — grep the asyncStorageResourceCache
//      callsites. The single-day branch could therefore never resolve on a
//      real device, only under a test that mocked the key.
// Hence: miss in cache => ask the server, and warm the cache for whoever
// reads it next. Offline with a warm cache behaves exactly as before; the
// network is touched only when the cache could not answer.
//
// Never silent: an unresolved template logs a tagged warning, so the failure
// is visible in logcat instead of being indistinguishable from "this session
// has no notes". Ids only, never note or exercise text (zero-PII logs).

const programsCache = asyncStorageResourceCache<ProgramT[]>('plan/programs');
const plansCache = asyncStorageResourceCache<PlanT[]>('plan/list');

type PlanTemplate =
  | {
      kind: 'program';
      program: ProgramT;
      weekIdx: number;
      sessionIdx: number;
    }
  | { kind: 'plan'; plan: PlanT };

/** The cue-bearing fields, resolved from either template kind. */
export type ResolvedTemplateSession = {
  entries: ReadonlyArray<PlannedExerciseEntry>;
  programDescription?: string;
  sessionNotes?: string;
};

type Source<T> = {
  cache: { read: () => Promise<T[] | null>; write: (v: T[]) => Promise<void> };
  path: string;
  parse: (raw: unknown) => T[];
  pick: (list: ReadonlyArray<T>) => T | null;
};

// Cache first; on a miss go to the server ONCE, parse with the same shared
// parser the Plan tab uses, and write the list back so the sibling surface
// (and the next session) find it warm. A network failure is not an error
// here — offline just means no cues, same as before.
async function findInSource<T>(
  src: Source<T>,
  apiFetch: ProgramApiFetch,
): Promise<T | null> {
  const cached = await src.cache.read();
  if (cached) {
    const hit = src.pick(cached);
    if (hit) return hit;
  }
  try {
    const parsed = src.parse(await apiFetch<unknown>(src.path));
    void src.cache.write(parsed);
    return src.pick(parsed);
  } catch {
    return null;
  }
}

/** Resolve the template a logged session came from, or null. */
async function loadPlanTemplate(
  fromPlanId: string,
  apiFetch: ProgramApiFetch,
): Promise<PlanTemplate | null> {
  const prog = parseProgramFromPlanId(fromPlanId);
  if (prog) {
    const program = await findInSource<ProgramT>(
      {
        cache: programsCache,
        path: '/api/me/programs',
        parse: parseProgramsList,
        pick: (list) => list.find((x) => x.id === prog.programId) ?? null,
      },
      apiFetch,
    );
    if (!program) {
      console.warn('[planCues] program template unresolved', {
        programId: prog.programId,
        weekIdx: prog.weekIdx,
        sessionIdx: prog.sessionIdx,
      });
      return null;
    }
    return {
      kind: 'program',
      program,
      weekIdx: prog.weekIdx,
      sessionIdx: prog.sessionIdx,
    };
  }

  const plan = await findInSource<PlanT>(
    {
      cache: plansCache,
      path: '/api/me/plans',
      parse: parsePlansList,
      pick: (list) => list.find((x) => x.id === fromPlanId) ?? null,
    },
    apiFetch,
  );
  if (!plan) {
    console.warn('[planCues] plan template unresolved', { planId: fromPlanId });
    return null;
  }
  return { kind: 'plan', plan };
}

/** Flatten either template kind into the fields the cue surfaces read. */
function resolveTemplateSession(
  tpl: PlanTemplate,
  locale?: Locale,
): ResolvedTemplateSession | null {
  if (tpl.kind === 'plan') {
    return {
      entries: tpl.plan.entries,
      ...(tpl.plan.notes ? { sessionNotes: tpl.plan.notes } : {}),
    };
  }
  const r = resolveProgramSession({
    program: tpl.program,
    weekIdx: tpl.weekIdx,
    sessionIdx: tpl.sessionIdx,
    locale,
  });
  if (!r) return null;
  return {
    entries: r.entries,
    ...(r.programDescription
      ? { programDescription: r.programDescription }
      : {}),
    ...(r.sessionNotes ? { sessionNotes: r.sessionNotes } : {}),
  };
}

/** Load + flatten in one step — what both cue hooks actually want. */
export async function loadTemplateSession(
  fromPlanId: string,
  apiFetch: ProgramApiFetch,
  // PE-21 — reader's language for the program's authored cue text.
  locale?: Locale,
): Promise<ResolvedTemplateSession | null> {
  const tpl = await loadPlanTemplate(fromPlanId, apiFetch);
  return tpl ? resolveTemplateSession(tpl, locale) : null;
}
